import {
  useState,
  useCallback,
  useRef,
  useEffect,
  ChangeEvent,
  DragEvent,
} from "react";
import { useSearchParams } from "react-router";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AssetFileViewer } from "@/modules/technicalDesigns/AssetFileViewer";
import {
  Trash2,
  Loader2,
  Upload,
  Pencil,
  Folder,
  FolderInput,
  File,
  FolderUp,
  FileUp,
  FileArchive,
  FolderPlus,
  FolderSync,
  MoreHorizontal,
  ExternalLink,
  Pause,
  Play,
  Unlink,
} from "lucide-react";
import { toast } from "sonner";
import { useFilesStore } from "./filesStore";
import { RenameFileDialog } from "./RenameFileDialog";
import { MoveFileDialog } from "./MoveFileDialog";
import { VaultBreadcrumbs } from "./VaultBreadcrumbs";
import { AddSyncedFolderSheet } from "./AddSyncedFolderSheet";
import { SyncedFolderStatusBadge } from "./SyncedFolderStatusBadge";
import {
  VAULT_SYNC_PROVIDER_LABELS,
  getVaultSyncDisplayState,
  useVaultSyncsStore,
} from "./vaultSyncsStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import {
  CreateOrganizationFolderParamsSchema,
  HydratedOrganizationFile,
  MAX_IMPORT_KEY_SEARCH_ENTRIES,
  ORGANIZATION_FILE_ENTRY_TYPE,
  VaultSync,
  buildImportDedupKey,
  chunkArray,
  isZipFile,
  resolveFileType,
} from "@forgepoint/shared";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { formatFileSize } from "@/lib/utils";
import {
  deleteAsset,
  uploadAsset,
  uploadAssets,
  UploadTransferError,
} from "@/lib/uploadAsset";
import { captureEvent, captureException } from "@/lib/analytics";
import {
  UploadEntry,
  UploadSelection,
  buildInputSelection,
  pickDirectory,
  readDroppedEntries,
  readPickedDirectory,
  supportsDirectoryPicker,
} from "@/lib/uploadSelection";
import {
  VaultRegistrationBatcher,
  VaultRegistrationProgress,
} from "./vaultRegistrationBatcher";

function reportUploadOutcome(
  totals: VaultRegistrationProgress,
  failedUploads: UploadEntry[],
  skippedBeforeUpload: number,
  addedFolderCount: number,
  retryFailedUploads: () => void,
): void {
  const skippedCount = totals.skippedExistingCount + skippedBeforeUpload;
  const added = [
    totals.registeredCount > 0 ? pluralizeFiles(totals.registeredCount) : null,
    addedFolderCount > 0 ? pluralizeFolders(addedFolderCount) : null,
  ].filter((part): part is string => part !== null);
  if (added.length > 0) {
    toast.success(`${added.join(" and ")} uploaded`);
  }
  if (skippedCount > 0) {
    reportSkippedFiles(skippedCount);
  }
  if (failedUploads.length > 0) {
    reportRetryableFailure(
      `Failed to upload ${pluralizeFiles(failedUploads.length)}: ${formatFailedFileNames(failedUploads.map(({ file }) => file.name))}`,
      retryFailedUploads,
    );
  }
  if (totals.failedCount > 0) {
    toast.error(
      `${pluralizeFiles(totals.failedCount)} uploaded to storage but not yet added to the Vault. Re-running the same upload is safe and will finish adding them.`,
    );
  }
}

const SKIPPED_FILES_TOAST_ID = "vault-upload-skipped";

/** Keeps unreadable paths visible while the readable entries continue uploading. */
function reportUnreadableEntries(selection: UploadSelection): void {
  const failures = selection.readFailures;
  if (!failures?.length) {
    return;
  }
  const paths = failures.map((failure) => failure.relativePath);

  toast.warning(
    `Skipped ${paths.length} unreadable file or folder${paths.length === 1 ? "" : "s"}`,
    {
      description: `These entries were excluded from the selection. Check and select them again: ${paths.slice(0, 5).join(", ")}${paths.length > 5 ? "…" : ""}`,
      duration: Infinity,
      closeButton: true,
    },
  );
  captureException(new Error("Folder scan skipped unreadable entries"), {
    tags: { stage: "folder_read", reason: "read_failure" },
    extra: {
      skippedCount: failures.length,
      readFailures: failures.slice(0, 100).map(({ relativePath, error }) => ({
        relativePath,
        message: String(error),
      })),
    },
  });
}

/** Reuses one toast id so the running skip count updates in place. */
function reportSkippedFiles(count: number): void {
  toast.info(`${pluralizeFiles(count)} skipped — already in the Vault`, {
    id: SKIPPED_FILES_TOAST_ID,
  });
}

const FAILED_UPLOAD_TOAST_ID = "vault-upload-failed";

/** Stays on screen until the user retries or dismisses it. */
function reportRetryableFailure(message: string, onRetry: () => void): void {
  toast.error(message, {
    id: FAILED_UPLOAD_TOAST_ID,
    duration: Infinity,
    action: { label: "Retry", onClick: onRetry },
  });
}

/** Records a failed upload, with the transfer details when the PUT failed. */
function captureUploadFailure(error: unknown, file: File): void {
  const transferError = error instanceof UploadTransferError ? error : null;
  captureException(
    error instanceof Error ? error : new Error("Upload failed"),
    {
      tags: transferError ? { uploadErrorKind: transferError.kind } : undefined,
      extra: {
        fileSize: file.size,
        status: transferError?.status,
        bytesSent: transferError?.bytesSent,
        elapsedMs: transferError?.elapsedMs,
      },
    },
  );
}

/** One event per "Upload folder" click, whatever the picker led to. */
const VAULT_FOLDER_PICK_EVENT = "vault_folder_pick";

function buildUploadStatusLabel(progress: VaultRegistrationProgress): string {
  const counts = [
    progress.registeredCount > 0
      ? `${progress.registeredCount.toLocaleString()} registered`
      : null,
    progress.skippedExistingCount > 0
      ? `${progress.skippedExistingCount.toLocaleString()} skipped`
      : null,
  ].filter((count): count is string => count !== null);

  return counts.length > 0
    ? `Uploading files (${counts.join(", ")})`
    : "Uploading files";
}

const MAX_FAILED_NAMES_IN_TOAST = 3;

function formatFailedFileNames(names: string[]): string {
  const shownNames = names.slice(0, MAX_FAILED_NAMES_IN_TOAST);
  const remainingCount = names.length - shownNames.length;
  return remainingCount > 0
    ? `${shownNames.join(", ")} and ${remainingCount} more`
    : shownNames.join(", ");
}

// Folder paths are short; 500 per request stays far below the 1MiB body limit.
const FOLDER_REGISTRATION_BATCH_SIZE = 500;

function pluralizeFiles(count: number): string {
  return `${count} file${count > 1 ? "s" : ""}`;
}

function pluralizeFolders(count: number): string {
  return `${count} folder${count > 1 ? "s" : ""}`;
}

/** Poll cadence while a synced folder is running or needs attention. */
const SYNC_POLL_INTERVAL_MS = 30_000;

/** A ZIP inside a dropped folder uploads as a file; a loose one is imported. */
function isTopLevelZip({ file, relativePath }: UploadEntry): boolean {
  return !relativePath.includes("/") && isZipFile(file.name, file.type);
}

interface FilesControllerProps {
  /** Whether the user may mutate the vault (holds `vault.edit`). */
  canEdit: boolean;
}

/**
 * Vault file browser. Lists organization files and, for users holding
 * `vault.edit`, exposes upload, folder, rename, move, and delete controls.
 * @param props - Whether the user may mutate the vault
 * @returns The vault file browser UI
 */
export default function FilesController({ canEdit }: FilesControllerProps) {
  const {
    files,
    fileLoadingStates,
    listLoading,
    listError,
    total,
    breadcrumbs,
    fetchFiles,
    searchImportKeys,
    addFiles,
    createFolder,
    importZip,
    deleteFiles,
  } = useFilesStore();
  const isSyncEnabled = useFeatureFlag("vault-synced-folders");
  const {
    syncs,
    byOrganizationFileId,
    status: syncRequestStates,
    fetchSyncs,
    updateSyncStatus,
    unlink,
  } = useVaultSyncsStore();

  const PAGE_SIZE = 50;
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolderId = searchParams.get("folderId");
  const [page, setPage] = useState(0);
  const [currentSearch, setCurrentSearch] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] =
    useState<HydratedOrganizationFile | null>(null);
  const [fileIdToRename, setFileIdToRename] = useState<string | null>(null);
  const [fileIdToMove, setFileIdToMove] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadStatusLabel, setUploadStatusLabel] = useState("Uploading files");
  const [isDragOver, setIsDragOver] = useState(false);
  const [droppedUpload, setDroppedUpload] = useState<UploadSelection | null>(
    null,
  );
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderNameError, setNewFolderNameError] = useState<string | null>(
    null,
  );
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [viewingFile, setViewingFile] =
    useState<HydratedOrganizationFile | null>(null);
  const [isAddSyncedFolderOpen, setIsAddSyncedFolderOpen] = useState(false);
  const [fileToUnlink, setFileToUnlink] =
    useState<HydratedOrganizationFile | null>(null);
  const currentSearchRef = useRef("");
  const currentFolderIdRef = useRef(currentFolderId);
  const syncedRootRunRef = useRef<{
    syncId: string;
    lastSuccessAt: string | null;
  } | null>(null);
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const getSyncForFile = (fileId: string): VaultSync | undefined => {
    const syncId = byOrganizationFileId[fileId];
    return isSyncEnabled && syncId ? syncs[syncId] : undefined;
  };
  const syncedRoot = breadcrumbs
    .map((breadcrumb) => getSyncForFile(breadcrumb.id))
    .find((sync): sync is VaultSync => sync !== undefined);
  const syncedRootId = syncedRoot?.id ?? null;
  const syncedRootLastSuccessAt = syncedRoot?.progress?.lastSuccessAt ?? null;
  const canEditHere = canEdit && !syncedRoot;
  const needsSyncPolling =
    isSyncEnabled &&
    Object.values(syncs).some((sync) => {
      const state = getVaultSyncDisplayState(sync);
      return state === "syncing" || state === "error";
    });

  const {
    entitiesArray: filesArray,
    handleSearch: baseHandleSearch,
    setDisplayedEntityIds: setDisplayedFileIds,
  } = useEntitySearch({
    entities: files,
    fetchEntities: (params) =>
      fetchFiles({
        search: params?.search,
        parentFolderId: currentFolderId,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const loadFilesPage = useCallback(
    async (nextPage: number, searchValue = currentSearchRef.current) => {
      const ids = await fetchFiles({
        search: searchValue || undefined,
        parentFolderId: currentFolderId,
        limit: PAGE_SIZE,
        offset: nextPage * PAGE_SIZE,
      });
      // Drop stale results: the user may have navigated while this was in flight.
      if (
        currentFolderIdRef.current !== currentFolderId ||
        currentSearchRef.current !== searchValue
      ) {
        return;
      }
      setDisplayedFileIds(ids);
    },
    [fetchFiles, currentFolderId, setDisplayedFileIds],
  );

  const refreshFiles = useCallback(
    async (nextPage = page) => {
      await loadFilesPage(nextPage);
    },
    [loadFilesPage, page],
  );

  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
    folderInputRef.current?.setAttribute("directory", "");
  }, []);

  useEffect(() => {
    currentSearchRef.current = currentSearch;
  }, [currentSearch]);

  useEffect(() => {
    currentFolderIdRef.current = currentFolderId;
  }, [currentFolderId]);

  useEffect(() => {
    setPage(0);
    loadFilesPage(0);
  }, [currentFolderId, loadFilesPage]);

  useEffect(() => {
    if (isSyncEnabled) {
      fetchSyncs();
    }
  }, [isSyncEnabled, fetchSyncs]);

  useEffect(() => {
    if (!needsSyncPolling) {
      return;
    }
    const interval = setInterval(fetchSyncs, SYNC_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [needsSyncPolling, fetchSyncs]);

  useEffect(() => {
    const previousRun = syncedRootRunRef.current;
    syncedRootRunRef.current = syncedRootId
      ? { syncId: syncedRootId, lastSuccessAt: syncedRootLastSuccessAt }
      : null;
    if (
      previousRun?.syncId !== syncedRootId ||
      previousRun.lastSuccessAt === syncedRootLastSuccessAt
    ) {
      return;
    }
    refreshFiles();
  }, [syncedRootId, syncedRootLastSuccessAt, refreshFiles]);

  const handleSearch = useCallback(
    (value: string) => {
      setPage(0);
      setCurrentSearch(value);
      baseHandleSearch(value, {
        parentFolderId: currentFolderId ?? undefined,
      });
    },
    [baseHandleSearch, currentFolderId],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      refreshFiles(newPage);
    },
    [refreshFiles],
  );

  const navigateToFolder = useCallback(
    (folderId: string | null) => {
      if (folderId) {
        setSearchParams({ folderId });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const handleDelete = (file: HydratedOrganizationFile) => {
    setFileToDelete(file);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) {
      return;
    }

    setIsDeleteDialogOpen(false);
    const deletingFile = fileToDelete;
    const deletingSyncedRoot = getSyncForFile(deletingFile.id) !== undefined;
    setFileToDelete(null);

    try {
      const success = await deleteFiles({
        organizationFileIds: [deletingFile.id],
      });
      if (success) {
        toast.success(
          deletingFile.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER
            ? "Folder deleted"
            : "File deleted",
        );
        if (deletingSyncedRoot) {
          fetchSyncs();
        }
        await refreshFiles();
      } else {
        toast.error("Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  /**
   * Sends the planned entries to the server in batches and drops the files it
   * already holds. A file is only skipped when its path, size, and type all
   * match an existing entry.
   */
  const skipFilesAlreadyInVault = useCallback(
    async (pickedFiles: UploadEntry[], parentFolderId: string | null) => {
      const entries = pickedFiles.map(({ file, relativePath }) => ({
        relativePath,
        fileSize: file.size,
        fileType: resolveFileType(file.name, file.type) ?? null,
      }));
      const entryKeys = entries.map((entry) =>
        buildImportDedupKey(entry.relativePath, entry.fileSize, entry.fileType),
      );
      const existingKeys = new Set<string>();
      for (const batch of chunkArray(entries, MAX_IMPORT_KEY_SEARCH_ENTRIES)) {
        const result = await searchImportKeys({
          parentFolderId,
          entries: batch,
        });
        // A failed lookup is safe to ignore: registration skips duplicates too.
        if (!result) {
          return { toUpload: pickedFiles, alreadyUploadedCount: 0 };
        }
        result.existingKeys.forEach((key) => existingKeys.add(key));
      }

      const toUpload = pickedFiles.filter(
        (_, index) => !existingKeys.has(entryKeys[index]),
      );
      return {
        toUpload,
        alreadyUploadedCount: pickedFiles.length - toUpload.length,
      };
    },
    [searchImportKeys],
  );

  /**
   * Creates the picked folders before the files upload, so a folder holding no
   * file still lands. Returns false when a batch failed.
   */
  const registerFolderPaths = useCallback(
    async (folderPaths: string[], parentFolderId: string | null) => {
      for (const batch of chunkArray(
        folderPaths,
        FOLDER_REGISTRATION_BATCH_SIZE,
      )) {
        const created = await addFiles({ parentFolderId, folderPaths: batch });
        if (!created) {
          return false;
        }
      }

      return true;
    },
    [addFiles],
  );

  const handleUpload = useCallback(
    async (
      selection: UploadSelection,
      destinationFolderId: string | null = currentFolderId,
    ) => {
      setIsUploading(true);
      setUploadStatusLabel("Checking for duplicates");

      const isViewingDestination = () =>
        currentFolderIdRef.current === destinationFolderId &&
        currentSearchRef.current === "";

      let uploadsFinished = false;
      const batcher = new VaultRegistrationBatcher({
        parentFolderId: destinationFolderId,
        addFiles,
        deleteAsset,
        onBatchSettled: async (progress) => {
          if (uploadsFinished) {
            setUploadStatusLabel(buildUploadStatusLabel(progress));
          }
          if (progress.skippedExistingCount > 0) {
            reportSkippedFiles(progress.skippedExistingCount);
          }
          if (isViewingDestination()) {
            await refreshFiles();
          }
        },
      });

      const failedUploads: UploadEntry[] = [];
      const { toUpload: fileArray, alreadyUploadedCount } =
        await skipFilesAlreadyInVault(selection.files, destinationFolderId);
      if (alreadyUploadedCount > 0) {
        reportSkippedFiles(alreadyUploadedCount);
      }
      setUploadStatusLabel("Uploading files");
      setUploadProgress(0);
      const totalBytes = fileArray.reduce(
        (sum, { file }) => sum + file.size,
        0,
      );
      const loadedBytesByFile = new Map<number, number>();
      let uploadedFileCount = 0;

      const updateAggregateProgress = () => {
        const loadedBytes = Array.from(loadedBytesByFile.values()).reduce(
          (sum, bytes) => sum + bytes,
          0,
        );
        setUploadProgress(
          totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 100,
        );
      };

      try {
        const foldersAdded = await registerFolderPaths(
          selection.folderPaths,
          destinationFolderId,
        );
        if (!foldersAdded) {
          toast.error("Failed to add folders to the Vault");
        }

        let registration = Promise.resolve();
        const results = await uploadAssets(
          fileArray.map(({ file }) => file),
          {
            onProgress: (index, progress) => {
              loadedBytesByFile.set(index, progress.loadedBytes);
              updateAggregateProgress();
            },
            onUploaded: (index, asset) => {
              uploadedFileCount++;
              setUploadStatusLabel(
                `Uploaded ${uploadedFileCount.toLocaleString()} of ${fileArray.length.toLocaleString()} files`,
              );
              registration = registration.then(() =>
                batcher.add({
                  assetId: asset.id,
                  relativePath: fileArray[index].relativePath,
                }),
              );
            },
          },
        );
        uploadsFinished = true;
        for (const [index, result] of results.entries()) {
          if (result.status === "rejected") {
            const { file, relativePath } = fileArray[index];
            captureUploadFailure(result.reason, file);
            failedUploads.push({ file, relativePath });
          }
        }

        setUploadStatusLabel("Saving uploaded files");
        await registration;
        const totals = await batcher.finish();
        const addedFolderCount = foldersAdded
          ? selection.folderPaths.length
          : 0;
        if (
          (totals.registeredCount > 0 || addedFolderCount > 0) &&
          isViewingDestination()
        ) {
          setPage(0);
          await refreshFiles(0);
        }
        reportUploadOutcome(
          totals,
          failedUploads,
          alreadyUploadedCount,
          addedFolderCount,
          () => {
            handleUpload(
              { files: failedUploads, folderPaths: [] },
              destinationFolderId,
            );
          },
        );
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        setUploadStatusLabel("Uploading files");
      }
    },
    [
      addFiles,
      currentFolderId,
      refreshFiles,
      registerFolderPaths,
      skipFilesAlreadyInVault,
    ],
  );

  const handleZipImport = useCallback(
    async (zipFile: File) => {
      setIsUploading(true);
      setUploadStatusLabel("Uploading ZIP");
      setUploadProgress(0);

      try {
        const asset = await uploadAsset(zipFile, {
          onProgress: (progress) => {
            setUploadProgress(progress.percent);
          },
        });
        setUploadStatusLabel("Importing ZIP");
        setUploadProgress(null);

        const result = await importZip({
          parentFolderId: currentFolderId,
          assetId: asset.id,
          deleteSourceAsset: false,
        });

        if (!result) {
          toast.error("Failed to import ZIP");
          return;
        }

        toast.success("ZIP import queued");
        setPage(0);
        await refreshFiles(0);
      } catch (error) {
        captureUploadFailure(error, zipFile);
        reportRetryableFailure(
          error instanceof Error ? error.message : "ZIP import failed",
          () => {
            handleZipImport(zipFile);
          },
        );
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        setUploadStatusLabel("Uploading files");
      }
    },
    [currentFolderId, importZip, refreshFiles],
  );

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }
    handleUpload(buildInputSelection(selectedFiles));
    e.target.value = "";
  };

  /** Shows the running file count while a picked folder or a drop is read. */
  const reportFilesRead = (fileCount: number) =>
    setUploadStatusLabel(`Reading ${pluralizeFiles(fileCount)}`);

  /**
   * Picks a folder through the directory picker where the browser has one, so
   * empty subfolders survive, and falls back to the folder input elsewhere.
   */
  const handleFolderUpload = async () => {
    if (!supportsDirectoryPicker()) {
      folderInputRef.current?.click();
      return;
    }

    const selection = await pickAndReadFolder();
    if (selection) {
      await handleUpload(selection);
    }
  };

  /**
   * Reads the picked folder behind the indicator, so the wait is visible from
   * the moment the person chooses. Returns null when nothing was read.
   */
  const pickAndReadFolder = async (): Promise<UploadSelection | null> => {
    try {
      const directory = await pickDirectory();
      if (!directory) {
        captureEvent(VAULT_FOLDER_PICK_EVENT, { outcome: "cancelled" });
        return null;
      }

      setIsUploading(true);
      setUploadStatusLabel("Reading folder");
      const startedAt = performance.now();
      const selection = await readPickedDirectory(directory, reportFilesRead);
      reportUnreadableEntries(selection);
      if (!selection.files.length && !selection.folderPaths.length) {
        captureEvent(VAULT_FOLDER_PICK_EVENT, { outcome: "failed" });
        setIsUploading(false);
        return null;
      }
      captureEvent(VAULT_FOLDER_PICK_EVENT, {
        outcome: "picked",
        fileCount: selection.files.length,
        folderCount: selection.folderPaths.length,
        readMs: Math.round(performance.now() - startedAt),
      });
      return selection;
    } catch (error) {
      setIsUploading(false);
      captureEvent(VAULT_FOLDER_PICK_EVENT, { outcome: "failed" });
      captureException(
        error instanceof Error ? error : new Error("Folder pick failed"),
        { tags: { stage: "folder_pick" } },
      );
      toast.error("Could not read that folder");
      return null;
    }
  };

  const handleFolderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }
    handleUpload(buildInputSelection(selectedFiles));
    e.target.value = "";
  };

  const handleZipChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    const zipFile = selectedFiles?.[0];
    if (!zipFile) {
      return;
    }
    handleZipImport(zipFile);
    e.target.value = "";
  };

  const dragDepthRef = useRef(0);

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (
        !canEditHere ||
        !e.dataTransfer.types.includes("Files") ||
        isUploading
      ) {
        return;
      }
      dragDepthRef.current += 1;
      if (dragDepthRef.current === 1) {
        setIsDragOver(true);
      }
    },
    [canEditHere, isUploading],
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setIsDragOver(false);

      if (!canEditHere || isUploading) {
        return;
      }

      setIsUploading(true);
      setUploadStatusLabel("Reading files");
      try {
        const selection = await readDroppedEntries(
          e.dataTransfer.items,
          reportFilesRead,
        );
        reportUnreadableEntries(selection);
        if (
          selection.files.length === 0 &&
          selection.folderPaths.length === 0
        ) {
          return;
        }

        setDroppedUpload(selection);
      } finally {
        setIsUploading(false);
      }
    },
    [canEditHere, isUploading],
  );

  const handleConfirmDropUpload = useCallback(async () => {
    if (!droppedUpload) {
      return;
    }

    const zipEntries = droppedUpload.files.filter(isTopLevelZip);
    const rest = droppedUpload.files.filter((entry) => !isTopLevelZip(entry));
    setDroppedUpload(null);

    for (const { file } of zipEntries) {
      await handleZipImport(file);
    }

    if (rest.length > 0 || droppedUpload.folderPaths.length > 0) {
      await handleUpload({
        files: rest,
        folderPaths: droppedUpload.folderPaths,
      });
    }
  }, [droppedUpload, handleUpload, handleZipImport]);

  const handleCreateFolder = async () => {
    const parsed = CreateOrganizationFolderParamsSchema.safeParse({
      parentFolderId: currentFolderId,
      name: newFolderName,
    });
    if (!parsed.success) {
      setNewFolderNameError(
        parsed.error.issues[0]?.message ?? "Invalid folder name",
      );
      return;
    }

    setNewFolderNameError(null);
    setIsCreatingFolder(true);
    try {
      const folder = await createFolder(parsed.data);
      if (folder) {
        toast.success("Folder created");
        setNewFolderName("");
        setIsCreateFolderDialogOpen(false);
        await refreshFiles();
      } else {
        toast.error("Failed to create folder");
      }
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleToggleSyncPaused = async (sync: VaultSync) => {
    const nextStatus = sync.status === "ENABLED" ? "DISABLED" : "ENABLED";
    const updated = await updateSyncStatus(sync.id, nextStatus);
    if (!updated) {
      toast.error(
        useVaultSyncsStore.getState().status[sync.id]?.error ??
          "Failed to update synced folder",
      );
      return;
    }
    toast.success(
      nextStatus === "DISABLED" ? "Syncing paused" : "Syncing resumed",
    );
  };

  const handleConfirmUnlink = async () => {
    const sync = fileToUnlink ? getSyncForFile(fileToUnlink.id) : undefined;
    setFileToUnlink(null);
    if (!sync) {
      return;
    }
    const success = await unlink(sync.id);
    if (!success) {
      toast.error(
        useVaultSyncsStore.getState().status[sync.id]?.error ??
          "Failed to unlink synced folder",
      );
      return;
    }
    toast.success("Folder unlinked");
  };

  const columns: DataTableColumn<HydratedOrganizationFile>[] = [
    {
      key: "name",
      header: "Name",
      format: (_value: unknown, file: HydratedOrganizationFile) => {
        const sync = getSyncForFile(file.id);
        return (
          <div className="flex items-center gap-2 min-w-0">
            {file.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER ? (
              <Folder className="h-4 w-4 shrink-0 text-amber-600" />
            ) : (
              <File className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {file.name}
            </span>
            {sync && <SyncedFolderStatusBadge sync={sync} />}
          </div>
        );
      },
    },
    {
      key: "fileType",
      header: "Type",
      className: "hidden sm:table-cell",
      format: (_value: unknown, file: HydratedOrganizationFile) =>
        file.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER
          ? "Folder"
          : file.asset?.fileType || "-",
    },
    {
      key: "fileSize",
      header: "Size",
      className: "hidden sm:table-cell",
      format: (_value: unknown, file: HydratedOrganizationFile) =>
        file.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER
          ? "-"
          : formatFileSize(file.asset?.fileSize),
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      className: "hidden sm:table-cell",
    },
    {
      key: "actions",
      header: "",
      width: 50,
      format: (_value: unknown, file: HydratedOrganizationFile) => {
        if (!canEditHere) {
          return null;
        }
        const sync = getSyncForFile(file.id);
        const syncRequestState = sync ? syncRequestStates[sync.id] : undefined;
        const isDeleting =
          fileLoadingStates.get(file.id)?.isLoadingDelete ||
          syncRequestState?.isLoadingUpdate ||
          syncRequestState?.isLoadingUnlink ||
          false;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => e.stopPropagation()}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MoreHorizontal className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setFileIdToRename(file.id);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setFileIdToMove(file.id);
                }}
              >
                <FolderInput className="h-4 w-4 mr-2" />
                Move
              </DropdownMenuItem>
              {sync && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSyncPaused(sync);
                    }}
                  >
                    {sync.status === "ENABLED" ? (
                      <Pause className="h-4 w-4 mr-2" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    {sync.status === "ENABLED"
                      ? "Pause syncing"
                      : "Resume syncing"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        sync.integrationConfig.webUrl,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in{" "}
                    {VAULT_SYNC_PROVIDER_LABELS[sync.vaultSyncProviderId]}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setFileToUnlink(file);
                    }}
                  >
                    <Unlink className="h-4 w-4 mr-2" />
                    Unlink
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(file);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const toolbarActions = !canEditHere ? undefined : (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsCreateFolderDialogOpen(true)}
      >
        <FolderPlus className="h-4 w-4 mr-1" />
        New folder
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" disabled={isUploading}>
            <Upload className="h-4 w-4 mr-1" />
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => filesInputRef.current?.click()}>
            <FileUp className="h-4 w-4 mr-2" />
            Upload files
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleFolderUpload}>
            <FolderUp className="h-4 w-4 mr-2" />
            Upload folder
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => zipInputRef.current?.click()}>
            <FileArchive className="h-4 w-4 mr-2" />
            Import ZIP
          </DropdownMenuItem>
          {isSyncEnabled && (
            <DropdownMenuItem onSelect={() => setIsAddSyncedFolderOpen(true)}>
              <FolderSync className="h-4 w-4 mr-2" />
              Add synced folder
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  if (listError) {
    return (
      <div className="p-8 text-red-600">Error loading files: {listError}</div>
    );
  }

  return (
    <div
      className="@container/vault flex flex-col h-full relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/5 border-2 border-dashed border-primary rounded-lg pointer-events-none animate-in fade-in duration-150">
          <div className="flex flex-col items-center gap-2 text-primary">
            <Upload className="h-10 w-10" />
            <span className="text-lg font-medium">
              Drop files here to upload
            </span>
          </div>
        </div>
      )}
      <div className="flex items-center gap-1 px-3 py-2 border-b bg-background text-sm">
        <VaultBreadcrumbs
          breadcrumbs={breadcrumbs}
          onNavigate={navigateToFolder}
        />
      </div>
      {syncedRoot && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/50 text-xs text-muted-foreground">
          <FolderSync className="h-3.5 w-3.5 shrink-0" />
          Synced from{" "}
          {VAULT_SYNC_PROVIDER_LABELS[syncedRoot.vaultSyncProviderId]} -
          read-only
        </div>
      )}

      <DataTable
        columns={columns}
        data={filesArray}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search vault..."
        className="flex-1 min-h-0"
        isLoading={listLoading && filesArray.length === 0}
        toolbarActions={toolbarActions}
        onClickRow={(file) => {
          if (file.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER) {
            navigateToFolder(file.id);
            return;
          }
          setViewingFile(file);
        }}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: handlePageChange,
        }}
      />

      <input
        ref={filesInputRef}
        type="file"
        onChange={handleFileChange}
        disabled={isUploading}
        className="hidden"
        multiple
      />

      <input
        ref={folderInputRef}
        type="file"
        onChange={handleFolderChange}
        disabled={isUploading}
        className="hidden"
        multiple
      />

      <input
        ref={zipInputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        onChange={handleZipChange}
        disabled={isUploading}
        className="hidden"
      />

      {isUploading && (
        <div className="fixed bottom-4 right-4 z-50 max-w-[calc(100vw-2rem)] flex items-center gap-2 bg-background border rounded-lg px-4 py-2 shadow-lg">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span className="min-w-0 break-words text-sm">
            <Upload className="h-4 w-4 inline mr-1" />
            {uploadStatusLabel}
            {uploadProgress !== null ? ` ${uploadProgress}%` : ""}
          </span>
        </div>
      )}

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        entityType={
          fileToDelete?.type === ORGANIZATION_FILE_ENTRY_TYPE.FOLDER
            ? "Folder"
            : "File"
        }
        itemName={fileToDelete?.name}
      />

      <RenameFileDialog
        fileId={fileIdToRename}
        onClose={() => setFileIdToRename(null)}
        onRenamed={(renamedFile) => {
          if (viewingFile?.id === renamedFile.id) {
            setViewingFile(renamedFile);
          }
          refreshFiles();
        }}
      />

      <MoveFileDialog
        fileId={fileIdToMove}
        onClose={() => setFileIdToMove(null)}
        onMoved={() => refreshFiles()}
      />

      {isSyncEnabled && (
        <AddSyncedFolderSheet
          open={isAddSyncedFolderOpen}
          onOpenChange={setIsAddSyncedFolderOpen}
          parentFolderId={currentFolderId}
          destinationName={
            breadcrumbs.length > 0
              ? breadcrumbs[breadcrumbs.length - 1].name
              : "Vault"
          }
          onCreated={() => {
            setPage(0);
            refreshFiles(0);
          }}
        />
      )}

      <AlertDialog
        open={fileToUnlink !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFileToUnlink(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="[overflow-wrap:anywhere]">
              Unlink "{fileToUnlink?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Files stay in Vault and stop syncing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUnlink}>
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={isCreateFolderDialogOpen}
        onOpenChange={(open) => {
          setIsCreateFolderDialogOpen(open);
          if (!open) {
            setNewFolderName("");
            setNewFolderNameError(null);
          }
        }}
      >
        <DialogContent>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleCreateFolder();
            }}
          >
            <DialogHeader>
              <DialogTitle>New folder</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-2">
              <Label htmlFor="vault-folder-name">Folder name</Label>
              <Input
                id="vault-folder-name"
                value={newFolderName}
                onChange={(event) => {
                  setNewFolderName(event.target.value);
                  setNewFolderNameError(null);
                }}
                disabled={isCreatingFolder}
                aria-invalid={newFolderNameError !== null}
                aria-describedby={
                  newFolderNameError ? "vault-folder-name-error" : undefined
                }
                autoFocus
              />
              {newFolderNameError && (
                <p
                  id="vault-folder-name-error"
                  className="text-xs text-destructive"
                >
                  {newFolderNameError}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateFolderDialogOpen(false)}
                disabled={isCreatingFolder}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!newFolderName.trim() || isCreatingFolder}
              >
                {isCreatingFolder ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Sheet
        open={!!viewingFile}
        onOpenChange={(open) => {
          if (!open) {
            setViewingFile(null);
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-3xl flex flex-col p-0">
          <SheetHeader className="px-6 py-4 border-b">
            <div className="flex items-center gap-2">
              <SheetTitle>{viewingFile?.name}</SheetTitle>
              {canEditHere && viewingFile && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFileIdToRename(viewingFile.id)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-hidden p-6">
            {viewingFile?.asset && (
              <AssetFileViewer
                asset={viewingFile.asset}
                className="h-full w-full"
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog
        open={droppedUpload !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDroppedUpload(null);
          }
        }}
      >
        <DialogContent>
          {(() => {
            const zipCount =
              droppedUpload?.files.filter(isTopLevelZip).length ?? 0;
            const fileCount = (droppedUpload?.files.length ?? 0) - zipCount;
            const folderCount = droppedUpload?.folderPaths.length ?? 0;
            const hasZips = zipCount > 0;
            const hasUploads = fileCount > 0 || folderCount > 0;

            const uploadSummary = [
              fileCount > 0 ? pluralizeFiles(fileCount) : null,
              folderCount > 0 ? pluralizeFolders(folderCount) : null,
            ]
              .filter((part): part is string => part !== null)
              .join(" and ");
            const zipSummary = `${zipCount} ZIP ${zipCount !== 1 ? "archives" : "archive"}`;

            const title =
              hasZips && hasUploads
                ? "Upload & import files"
                : hasZips
                  ? "Import ZIP"
                  : fileCount > 0
                    ? "Upload files"
                    : "Upload folders";

            const description =
              hasZips && hasUploads
                ? `Import ${zipSummary} and upload ${uploadSummary} to the vault?`
                : hasZips
                  ? `Import and extract ${zipSummary} to the vault?`
                  : `Upload ${uploadSummary} to the vault?`;

            const buttonLabel =
              hasZips && hasUploads
                ? "Upload & import"
                : hasZips
                  ? "Import"
                  : "Upload";

            return (
              <>
                <DialogHeader>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <div className="max-h-60 overflow-y-auto space-y-1 py-2">
                  {droppedUpload?.folderPaths
                    .slice(0, 100)
                    .map((folderPath) => (
                      <div
                        key={`folder-${folderPath}`}
                        className="flex items-center gap-2 text-sm px-1"
                      >
                        <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{folderPath}</span>
                      </div>
                    ))}
                  {droppedUpload?.files.slice(0, 100).map((entry, index) => (
                    <div
                      key={`${entry.relativePath}-${index}`}
                      className="flex items-center gap-2 text-sm px-1"
                    >
                      {isZipFile(entry.file.name, entry.file.type) ? (
                        <FileArchive className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <File className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate">{entry.relativePath}</span>
                      {isTopLevelZip(entry) && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          (extract)
                        </span>
                      )}
                      <span className="text-muted-foreground shrink-0 ml-auto">
                        {formatFileSize(entry.file.size)}
                      </span>
                    </div>
                  ))}
                </div>
                {(folderCount > 100 || fileCount + zipCount > 100) && (
                  <p className="text-sm text-muted-foreground">
                    Preview shortened. The full selection will be uploaded or
                    imported.
                  </p>
                )}
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDroppedUpload(null)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleConfirmDropUpload}>
                    <Upload className="h-4 w-4 mr-1" />
                    {buttonLabel}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
